import palette from "assets/theme/base/palette";
import { Card, Grid, Icon, Chip, Divider } from "@mui/material";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useEffect, useState } from "react";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController, setProfile } from "context";
import Dialog from "@mui/material/Dialog";
import PersonIcon from "@mui/icons-material/Person";
import Avatar from "@mui/material/Avatar";
import ChangePassword from "./ChangePassword";
import ManageSubscription from "./ManageSubscription";
import { call } from "services/axiosService/core";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
};

const calculateAge = (dob) => {
  if (!dob) return null;
  try {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age > 0 ? age : null;
  } catch {
    return null;
  }
};

const StatItem = ({ icon, label, value }) => (
  <MDBox display="flex" alignItems="center" gap={1.5} flex={1}>
    <MDBox
      sx={{
        width: 40, height: 40, borderRadius: "50%",
        bgcolor: `${palette.brand[500]}15`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}
    >
      <Icon sx={{ color: palette.brand[500], fontSize: "20px !important" }}>{icon}</Icon>
    </MDBox>
    <MDBox>
      <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
        {label}
      </MDTypography>
      <MDTypography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.4 }}>
        {value}
      </MDTypography>
    </MDBox>
  </MDBox>
);

const AthleteProfile = () => {
  const [name, setName] = useState("");
  const [guardian, setGuardian] = useState("");
  const [openProfilePictureModal, setOpenProfilePictureModal] = useState(false);
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const [openChangePasswordDialog, setOpenChangePasswordDialog] = useState(false);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const { t } = useTranslation();

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  useEffect(() => {
    setName(profile.details.name);
  }, []);

  useEffect(() => {
    const hasParents = profile.relationships?.parents?.length > 0;
    const hasCoaches = profile.relationships?.coaches?.length > 0;
    if (hasParents || hasCoaches) {
      call(`users/${profile.id}`, "GET", { params: { type: "guardian" } })
        .then((res) => {
          if (res.status === 200 && res.data?.length > 0) {
            setGuardian(res.data[0].details.name);
          }
        })
        .catch((err) => { Sentry.captureException(err); });
    }
  }, []);

  const firstName = name.split(" ")[0] || "";
  const memberSince = formatDate(profile.signup_at || profile.created_at || profile.signup_date);
  const age = calculateAge(profile.details?.dob);
  const gender = profile.details?.gender;

  const actionCards = [
    {
      icon: "lock",
      label: t("buttonTitles.changePassword"),
      description: t("profileInfo.changePasswordDesc"),
      color: palette.brand[500],
      bg: `${palette.brand[500]}18`,
      onClick: () => setOpenChangePasswordDialog(true),
    },
    {
      icon: "email",
      label: t("headings.manageSubscription"),
      description: t("profileInfo.manageSubscriptionDesc"),
      color: "#9c27b0",
      bg: "#9c27b018",
      onClick: () => setOpenSubscriptionDialog(true),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.profile", { name: firstName })} />

      {/* Profile Card */}
      <Card sx={{ p: { xs: 2.5, sm: 4 }, mt: 3, mb: 2.5, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
        <MDBox display="flex" alignItems="flex-start" gap={{ xs: 2, sm: 4 }} flexWrap="wrap">

          {/* Avatar */}
          <MDBox sx={{ position: "relative", flexShrink: 0, alignSelf: "center" }}>
            {profile.profile_image_url ? (
              <MDAvatar
                src={profile.profile_image_url}
                alt={name}
                sx={{ width: 130, height: 130, borderRadius: "50%", border: `3px solid ${palette.brand[100]}` }}
              />
            ) : (
              <Avatar sx={{ width: 130, height: 130, borderRadius: "50%", bgcolor: `${palette.brand[500]}18`, border: `3px solid ${palette.brand[100]}` }}>
                <PersonIcon sx={{ width: 80, height: 80, color: palette.brand[400] }} />
              </Avatar>
            )}
            <MDBox
              onClick={() => setOpenProfilePictureModal(true)}
              sx={{
                position: "absolute", bottom: 4, right: 4,
                width: 34, height: 34, borderRadius: "50%",
                bgcolor: palette.brand[500], display: "flex",
                alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                border: "2px solid #fff",
                "&:hover": { bgcolor: palette.brand[600] },
              }}
            >
              <Icon sx={{ color: "#fff", fontSize: "16px !important" }}>photo_camera</Icon>
            </MDBox>
          </MDBox>

          {/* Info section */}
          <MDBox flex={1} minWidth={220}>
            {/* Name + badge */}
            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
              <MDTypography variant="h4" fontWeight="bold">{name}</MDTypography>
              <Chip
                label={t("profileInfo.athlete")}
                size="small"
                sx={{
                  bgcolor: `${palette.brand[500]}18`, color: palette.brand[600],
                  fontWeight: "bold", fontSize: "0.72rem", height: 24, borderRadius: "6px",
                }}
              />
            </MDBox>

            {/* Email */}
            <MDBox display="flex" alignItems="flex-start" gap={1} mb={1}>
              <Icon sx={{ fontSize: "18px !important", color: "text.secondary", flexShrink: 0, mt: 0.25 }}>mail_outline</Icon>
              <MDTypography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{profile.email}</MDTypography>
            </MDBox>

            {/* Guardian */}
            {guardian && (
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <Icon sx={{ fontSize: "18px !important", color: "text.secondary" }}>people_outline</Icon>
                <MDTypography variant="body2" color="text.secondary">
                  {t("profileInfo.guardian")}:{" "}
                  <MDTypography component="span" variant="body2" fontWeight="bold" color="text.primary">
                    {guardian}
                  </MDTypography>
                </MDTypography>
              </MDBox>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Stats row */}
            <MDBox display="flex" flexWrap="wrap" gap={2}>
              {age !== null && (
                <StatItem icon="person_outline" label={t("tableHeaders.age")} value={`${age} ${t("profileInfo.yearsOld")}`} />
              )}
              {gender && (
                <StatItem
                  icon="male"
                  label={t("tableHeaders.gender")}
                  value={t(`userDynamicData.${gender.toLowerCase()}`) || gender}
                />
              )}
              {guardian && (
                <StatItem icon="supervisor_account" label={t("profileInfo.guardian")} value={guardian} />
              )}
              {memberSince && (
                <StatItem icon="calendar_today" label={t("profileInfo.memberSince")} value={memberSince} />
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Account Actions */}
      <MDTypography variant="h5" fontWeight="bold" mb={1.5}>
        {t("profileInfo.accountActions")}
      </MDTypography>

      <Grid container spacing={2} mb={2.5}>
        {actionCards.map((card) => (
          <Grid item xs={12} sm={6} key={card.label}>
            <Card
              onClick={card.onClick}
              sx={{
                p: 2.5, borderRadius: 3, cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                transition: "box-shadow 200ms, transform 200ms",
                "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.12)", transform: "translateY(-2px)" },
              }}
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDBox
                  sx={{
                    width: 52, height: 52, borderRadius: "14px",
                    bgcolor: card.bg, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: card.color, fontSize: "26px !important" }}>{card.icon}</Icon>
                </MDBox>
                <MDBox flex={1}>
                  <MDTypography variant="body2" fontWeight="bold" mb={0.25}>
                    {card.label}
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {card.description}
                  </MDTypography>
                </MDBox>
                <Icon sx={{ color: "text.disabled", fontSize: "20px !important", flexShrink: 0 }}>arrow_forward</Icon>
              </MDBox>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Support card */}
      <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", mb: 2.5 }}>
        <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <MDBox
            sx={{
              width: 52, height: 52, borderRadius: "14px",
              bgcolor: `${palette.brand[500]}18`, display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <Icon sx={{ color: palette.brand[500], fontSize: "26px !important" }}>verified_user</Icon>
          </MDBox>
          <MDBox flex={1} minWidth={200}>
            <MDTypography variant="body2" fontWeight="bold" mb={0.25}>
              {t("profileInfo.weAreHereTitle")}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              {t("profileInfo.weAreHereText")}
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            sx={{
              borderRadius: "10px", textTransform: "none", borderColor: palette.brand[500],
              color: palette.brand[500], fontWeight: "bold", px: 2.5, py: 1,
              flexShrink: 0,
              "&:hover": { bgcolor: `${palette.brand[500]}10`, borderColor: palette.brand[600] },
            }}
          >
            <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>headset_mic</Icon>
            {t("profileInfo.contactSupportBtn")}
          </MDButton>
        </MDBox>
      </Card>

      {/* Dialogs */}
      {openProfilePictureModal && (
        <ProfilePictureDialog
          openProfilePictureModal={openProfilePictureModal}
          closeProfilePictureModal={() => setOpenProfilePictureModal(false)}
          profile={profile}
          t={t}
        />
      )}

      {openChangePasswordDialog && (
        <ChangePassword
          openChangePasswordDialog={openChangePasswordDialog}
          setOpenChangePasswordDialog={setOpenChangePasswordDialog}
          setOpenSnackbar={setOpenSnackbar}
          setSeverity={setSeverity}
          setAlertMessage={setAlertMessage}
        />
      )}

      {openSubscriptionDialog && (
        <ManageSubscription
          openSubscriptionDialog={openSubscriptionDialog}
          setOpenSubscriptionDialog={setOpenSubscriptionDialog}
          setOpenSnackbar={setOpenSnackbar}
          setSeverity={setSeverity}
          setAlertMessage={setAlertMessage}
        />
      )}

      <Snackbar
        autoHideDuration={5000}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={severity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

const ProfilePictureDialog = ({ openProfilePictureModal, closeProfilePictureModal, profile, t }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [, dispatch] = useMaterialUIController();

  const removePhotoHandler = () => {
    setIsLoading(true);
    profile.profile_image_url = null;
    call(`users/${profile.id}`, "PUT", { body: profile })
      .then((res) => {
        if (res.status === 200) {
          setProfile(dispatch, profile);
          setIsLoading(false);
          closeProfilePictureModal();
        } else {
          setIsLoading(false);
          closeProfilePictureModal();
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setIsLoading(false);
        closeProfilePictureModal();
      });
  };

  const uploadPhotoHandler = (e) => {
    setIsUploading(true);
    const data = new FormData();
    data.append("file", e.target.files[0]);
    call(`users/${profile.id}`, "PUT", {
      headers: { accept: "application/json", "Content-Type": "multipart/form-data" },
      body: data,
      params: { upload: "image" },
    })
      .then((res) => {
        if (res.status === 200) {
          profile.profile_image_url = res.data.profile_image_url;
          setProfile(dispatch, profile);
          setIsUploading(false);
          closeProfilePictureModal();
        } else {
          setIsUploading(false);
          closeProfilePictureModal();
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setIsUploading(false);
        closeProfilePictureModal();
      });
  };

  return (
    <Dialog open={openProfilePictureModal} onClose={closeProfilePictureModal} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: "24px", overflowX: "hidden" } }}>

      <MDBox onClick={closeProfilePictureModal} sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, width: 36, height: 36, borderRadius: "50%", bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: palette.neutral[200] } }}>
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
        <MDBox sx={{ position: "relative", display: "inline-flex", mb: 2.5 }}>
          {profile.profile_image_url ? (
            <MDAvatar src={profile.profile_image_url} alt={t("imageAlts.athleteImage")} sx={{ width: 110, height: 110, borderRadius: "50%", border: `3px solid ${palette.brand[100]}` }} />
          ) : (
            <Avatar sx={{ width: 110, height: 110, borderRadius: "50%", bgcolor: palette.brand[50], border: `3px solid ${palette.brand[100]}` }}>
              <PersonIcon sx={{ width: 70, height: 70, color: palette.brand[400] }} />
            </Avatar>
          )}
          <MDBox
            onClick={() => document.getElementById("athlete-getFile").click()}
            sx={{ position: "absolute", bottom: 2, right: 2, width: 32, height: 32, borderRadius: "50%", bgcolor: palette.brand[500], display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.2)", "&:hover": { bgcolor: palette.brand[600] } }}
          >
            <Icon sx={{ color: "#fff", fontSize: "16px !important" }}>photo_camera</Icon>
          </MDBox>
        </MDBox>

        <MDTypography variant="h5" fontWeight="bold" mb={0.5}>{t("headings.uploadProfilePicture")}</MDTypography>
        <MDTypography variant="body2" color="text.secondary" mb={2.5}>{t("headings.profilePicture")}</MDTypography>

        <MDBox display="flex" flexDirection="column" gap={1.5}>
          <MDButton variant="contained" fullWidth onClick={() => document.getElementById("athlete-getFile").click()}
            sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold", "&:hover": { bgcolor: palette.brand[600] } }}>
            <Icon sx={{ mr: 1, fontSize: "18px !important" }}>upload</Icon>
            {isUploading ? t("buttonTitles.uploading") : t("buttonTitles.uploadPhoto")}
          </MDButton>
          {profile.profile_image_url && (
            <MDButton variant="outlined" fullWidth onClick={removePhotoHandler}
              sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", borderColor: "#f44336", color: "#f44336", fontWeight: "bold" }}>
              {isLoading ? t("buttonTitles.removing") : t("buttonTitles.removePhoto")}
            </MDButton>
          )}
        </MDBox>
        <input type="file" id="athlete-getFile" accept=".png,.jpg,.jpeg,.svg,.ico,.gif" onChange={uploadPhotoHandler} style={{ display: "none" }} />
      </MDBox>
    </Dialog>
  );
};

export default AthleteProfile;
